import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  bookingsData, getRoomById, getStructureById, getPharmacyById
} from "@/data/platformData";
import { getServiceById, serviceBundlesData, doctorsData } from "@/data/mockData";
import {
  Calendar, ArrowLeft, MapPin, Clock, ClipboardList, Plus, Trash2, CalendarClock, Bell
} from "lucide-react";

const CURRENT_PATIENT_ID = "1";

const statusBadge = (s) => ({
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  completed: "bg-sky-50 text-sky-700 border-sky-200",
  rescheduled: "bg-indigo-50 text-indigo-700 border-indigo-200"
}[s] || "bg-slate-50 text-slate-700 border-slate-200");

const statusLabel = (s) => ({
  confirmed: "Confermato",
  pending: "In attesa",
  cancelled: "Annullato",
  completed: "Completato",
  rescheduled: "Spostato"
}[s] || s);

export const PatientBookingsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(
    bookingsData.filter(b => b.patientId === CURRENT_PATIENT_ID)
  );
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDate, setMoveDate] = useState("");
  const [moveTime, setMoveTime] = useState("");

  const cancelBooking = (id) => {
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status: "cancelled" } : b));
    toast.success("Prenotazione annullata", { description: "Riceverai conferma via email." });
  };

  const openMove = (b) => {
    setMoveTarget(b);
    setMoveDate(b.date);
    setMoveTime(b.time);
  };

  const confirmMove = () => {
    setBookings(prev => prev.map(b => b.id === moveTarget.id ? { ...b, status: "rescheduled", date: moveDate, time: moveTime } : b));
    toast.success("Prenotazione spostata", { description: `Nuovo appuntamento: ${moveDate} alle ${moveTime}.` });
    setMoveTarget(null);
  };

  const renderBookingCard = (b) => {
    const room = getRoomById(b.diagnosticRoomId);
    const pharmacy = getPharmacyById(b.pharmacyId);
    const structure = b.structureId ? getStructureById(b.structureId) : null;
    const specialist = b.specialistId ? doctorsData.find(d => d.id === b.specialistId) : null;
    const services = b.bundleId
      ? [serviceBundlesData.find(x => x.id === b.bundleId)?.name].filter(Boolean)
      : b.serviceIds.map(sid => getServiceById(sid)?.name).filter(Boolean);

    return (
      <Card key={b.id} data-testid={`patient-booking-${b.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-sm flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-emerald-600" />
                {new Date(b.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                <span className="text-muted-foreground font-normal">alle {b.time}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> Durata stimata {b.duration} min
              </p>
            </div>
            <Badge variant="outline" className={statusBadge(b.status)}>{statusLabel(b.status)}</Badge>
          </div>

          <div className="flex flex-wrap gap-1">
            {services.map(s => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
          </div>

          <div className="grid gap-1.5 text-xs text-muted-foreground">
            <div className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{pharmacy?.name} · {room?.name}</span>
            </div>
            {structure && (
              <div className="inline-flex items-center gap-1">
                <Bell className="h-3 w-3 shrink-0" />
                <span className="truncate">Refertazione: {structure.name}</span>
              </div>
            )}
            {specialist && (
              <div className="truncate">Specialista: {specialist.name}</div>
            )}
            {b.notes && (
              <div className="italic">Note: {b.notes}</div>
            )}
          </div>

          {(b.status === "confirmed" || b.status === "pending" || b.status === "rescheduled") && (
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => openMove(b)} className="gap-1" data-testid={`patient-booking-move-${b.id}`}>
                <CalendarClock className="h-3 w-3" /> Sposta
              </Button>
              <Button variant="outline" size="sm" className="gap-1 text-destructive hover:text-destructive" onClick={() => cancelBooking(b.id)} data-testid={`patient-booking-cancel-${b.id}`}>
                <Trash2 className="h-3 w-3" /> Annulla
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const upcoming = bookings.filter(b => ["confirmed", "pending", "rescheduled"].includes(b.status))
    .sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));
  const past = bookings.filter(b => ["completed", "cancelled"].includes(b.status));

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/patient")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Home
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ClipboardList className="h-4 w-4 text-emerald-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Le mie prenotazioni</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Appuntamenti</h1>
            <p className="text-sm text-muted-foreground mt-1">{upcoming.length} attivi · {past.length} archiviati</p>
          </div>
          <Button variant="premium" onClick={() => navigate(`/booking?from=citizen&patientId=${CURRENT_PATIENT_ID}`)} className="gap-2" data-testid="patient-bookings-new-btn">
            <Plus className="h-4 w-4" /> Prenota
          </Button>
        </div>
      </div>

      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="patient-bookings-tab-upcoming">Prossimi ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="past" data-testid="patient-bookings-tab-past">Archivio ({past.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="space-y-3">
          {upcoming.length === 0 && (
            <Card className="border-dashed border-2">
              <CardContent className="p-8 text-center">
                <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium mb-1">Nessun appuntamento in arrivo</p>
                <Button variant="link" onClick={() => navigate(`/booking?from=citizen&patientId=${CURRENT_PATIENT_ID}`)}>Prenota una prestazione</Button>
              </CardContent>
            </Card>
          )}
          {upcoming.map(renderBookingCard)}
        </TabsContent>
        <TabsContent value="past" className="space-y-3">
          {past.length === 0 && (
            <Card className="border-dashed border-2">
              <CardContent className="p-8 text-center text-sm text-muted-foreground">Nessun appuntamento archiviato.</CardContent>
            </Card>
          )}
          {past.map(renderBookingCard)}
        </TabsContent>
      </Tabs>

      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sposta appuntamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nuova data</Label>
              <Input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)} data-testid="patient-booking-move-date" />
            </div>
            <div>
              <Label>Nuovo orario</Label>
              <Input type="time" value={moveTime} onChange={(e) => setMoveTime(e.target.value)} data-testid="patient-booking-move-time" />
            </div>
            <p className="text-xs text-muted-foreground">Riceverai conferma via email e SMS.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveTarget(null)}>Annulla</Button>
            <Button onClick={confirmMove} data-testid="patient-booking-move-confirm">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientBookingsPage;
